import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { ArrowDownRightIcon, ArrowUpRightIcon, Link2Icon, UnlinkIcon } from 'lucide-react'
import { EmptyState } from '@/components/empty-state'
import { BACKLINK_CHANGE_DAYS, type DomainStatus, useBacklinkProfile } from '@/lib/backlinks'
import { formatCount } from '@/lib/format'
import { cn } from '@/lib/utils'
import { MetricsSkeleton, TableSkeleton, useSiteScope } from '../-section'

/**
 * The link profile is one set read four ways, so it grows a sidebar for the
 * same reason keywords did: gained, lost and dead were a strip of buttons over
 * a table, which made them look like a control on the table rather than the
 * four questions the section exists to answer. Each is now a page, so it can be
 * linked to, opened in a tab and land with its own sort and its own paging.
 *
 * The layout is pathless so a single referring domain stays out of it — a
 * detail page is one click deep and has no business being narrowed by a nav it
 * cannot use.
 */
export const Route = createFileRoute('/_app/$org/$host/backlinks/_list')({
  component: BacklinksLayout,
})

const SECTIONS = [
  { to: '/$org/$host/backlinks', key: 'backlinks', label: 'Linking now', icon: Link2Icon },
  {
    to: '/$org/$host/backlinks/gained',
    key: 'gained',
    label: `Gained (${BACKLINK_CHANGE_DAYS}d)`,
    icon: ArrowUpRightIcon,
  },
  {
    to: '/$org/$host/backlinks/lost',
    key: 'lost',
    label: `Lost (${BACKLINK_CHANGE_DAYS}d)`,
    icon: ArrowDownRightIcon,
  },
  { to: '/$org/$host/backlinks/dead', key: 'dead', label: 'Dead links', icon: UnlinkIcon },
] as const

/** Which facet each entry counts. The nav carries the number because how many
 * domains are new and how many have gone is itself the question — a nav of
 * bare labels would hide the answer behind a click. */
const FACET: Record<(typeof SECTIONS)[number]['key'], DomainStatus> = {
  backlinks: 'live',
  gained: 'gained',
  lost: 'lost',
  dead: 'broken',
}

function BacklinksLayout() {
  const { org, host, site } = useSiteScope()
  const { data, isPending } = useBacklinkProfile(site)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const current = pathname.replace(/\/$/, '').split('/').at(-1)

  // Nothing collected is a state of the whole section, not of one facet, so it
  // answers here rather than four times over with a nav beside it offering
  // three more ways to read the same nothing.
  if (isPending || !data) {
    return (
      <div className="space-y-4">
        <MetricsSkeleton />
        <TableSkeleton />
      </div>
    )
  }

  if (data.state !== 'ready') {
    return (
      <div className="rounded-lg border border-border bg-card">
        {data.state === 'collecting' ? (
          <EmptyState
            title="Reading the link profile"
            description={`${host} is being pulled for the first time. The whole history arrives in one go, so the chart usually fills in within a minute.`}
          />
        ) : (
          <EmptyState
            title="Nothing collected yet"
            description={`No link profile has been collected for ${host} yet. It will appear here once it has been.`}
          />
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8 sm:flex-row">
      <nav className="flex flex-wrap gap-1 sm:w-44 sm:shrink-0 sm:flex-col sm:flex-nowrap">
        {SECTIONS.map((section) => {
          const active = current === section.key
          return (
            <Link
              key={section.key}
              to={section.to}
              params={{ org, host }}
              className={cn(
                'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
                active
                  ? 'bg-plum-selected text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <section.icon className={cn('size-3.5', active ? 'text-plum' : 'opacity-70')} />
              <span className="min-w-0 truncate">{section.label}</span>
              <span className="ml-auto pl-2 font-mono text-[11px] text-muted-foreground tabular">
                {formatCount(data.facets[FACET[section.key]])}
              </span>
            </Link>
          )
        })}
      </nav>

      <div className="min-w-0 flex-1 space-y-4">
        <Outlet />
      </div>
    </div>
  )
}
